// Copyright © 2019-2026 Focal Systems. All rights reserved.

import { useNavigate } from "@tanstack/react-router";
import { useCallback, useMemo } from "react";
import {
  ACTIVITY_FILTER_DEFAULTS,
  type ActivityEntry,
  type ActivityFilterState,
  ActivityTimeline,
} from "@/components/shared/activity-timeline";
import { decodeFilters, encodeFilters } from "@/lib/filter-params";

function generateMockData(): ActivityEntry[] {
  const now = Date.now();
  const day = 86_400_000;
  return [
    {
      id: 1,
      timestamp: new Date(now - 0.4 * day).toISOString(),
      user: "admin@focal.systems",
      action: "updated",
      target: "User",
      field: "defaultStore",
      oldValue: "acme_store_12",
      newValue: "acme_store_15",
    },
    {
      id: 2,
      timestamp: new Date(now - 1 * day).toISOString(),
      user: "admin@focal.systems",
      action: "updated",
      target: "Groups",
      field: "ops",
      oldValue: "member",
      newValue: "removed",
    },
    {
      id: 3,
      timestamp: new Date(now - 2.5 * day).toISOString(),
      user: "admin@focal.systems",
      action: "activated",
      target: "User",
    },
    {
      id: 4,
      timestamp: new Date(now - 5 * day).toISOString(),
      user: "admin@focal.systems",
      action: "updated",
      target: "Groups",
      field: "admin",
      oldValue: "—",
      newValue: "member",
      message: "Promoted to admin group per manager request.",
    },
    {
      id: 5,
      timestamp: new Date(now - 10 * day).toISOString(),
      user: "admin@focal.systems",
      action: "deactivated",
      target: "User",
      message: "User on extended leave.",
    },
    {
      id: 6,
      timestamp: new Date(now - 20 * day).toISOString(),
      user: "ops@focal.systems",
      action: "updated",
      target: "User",
      field: "email",
      oldValue: "john.doe@acme.com",
      newValue: "j.doe@acme.com",
    },
    {
      id: 7,
      timestamp: new Date(now - 35 * day).toISOString(),
      user: "admin@focal.systems",
      action: "updated",
      target: "User",
      field: "fullName",
      oldValue: "John Doe",
      newValue: "John A. Doe",
    },
    {
      id: 8,
      timestamp: new Date(now - 50 * day).toISOString(),
      user: "ops@focal.systems",
      action: "updated",
      target: "Groups",
      field: "viewer",
      oldValue: "—",
      newValue: "member",
    },
    {
      id: 9,
      timestamp: new Date(now - 70 * day).toISOString(),
      user: "admin@focal.systems",
      action: "created",
      target: "User",
    },
  ];
}

const MOCK_DATA = generateMockData();

interface UserActivityTabProps {
  publicId: string;
  initialFilters?: string;
}

export function UserActivityTab({ publicId, initialFilters }: UserActivityTabProps) {
  const navigate = useNavigate();
  const initial = useMemo(() => decodeFilters(initialFilters, ACTIVITY_FILTER_DEFAULTS), [initialFilters]);

  const handleFilterChange = useCallback(
    (values: ActivityFilterState) => {
      const encoded = encodeFilters(values, ACTIVITY_FILTER_DEFAULTS);
      navigate({
        to: "/iam/users/$publicId",
        params: { publicId },
        search: { t: "activity", ...(encoded ? { f: encoded } : {}) },
        replace: true,
      });
    },
    [navigate, publicId],
  );

  return <ActivityTimeline entries={MOCK_DATA} initialFilters={initial} onFilterChange={handleFilterChange} />;
}
